import React, { useState, useRef, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { usePopper } from "react-popper";
import { FaChartLine } from "react-icons/fa";
import domtoimage from "dom-to-image";
import Button from "../../components/buttons/Button";
import { FaCopy, FaDownload, FaTimes } from "react-icons/fa";
import Notification from "../../components/Notifications/Notification";
import useNotification from "../../components/Notifications/useNotifications";
import { createLogger } from "../../services/Debug";
// Import the original BullseyeDiagram components
import OriginalBullseyeDiagram, { 
  EchoReportView as OriginalEchoReportView,
  renderBullseyeImage as originalRenderBullseyeImage
} from './BullseyeDiagram';

/* -----------------------------------------  -------------------------
   Data and Constants
------------------------------------------------------------------ */

const ANOMALIES = [
  "Normal",
  "Hyperkinésie",
  "Hypokinésie",
  "Akinésie",
  "Dyskinésie",
  "Anévrysme",
  "Non évaluable",
];

const ANOMALY_COLORS = {
  "Normal": "#4ade80",
  "Hyperkinésie": "#e0b0ff",
  "Hypokinésie": "#fbbf24",
  "Akinésie": "#ef4444",
  "Dyskinésie": "#a78bfa",
  "Anévrysme": "#6b7280",
  "Non évaluable": "#f3f4f6"
};

// Conclusion anomalies for the 5th bullseye
const CONCLUSION_ANOMALIES = [
  "Normal",
  "Non viable",
  "Ischémie",
  "Hibernation",
  "Non évaluable"
];

const CONCLUSION_ANOMALY_COLORS = {
  "Normal": "#4ade80",
  "Non viable": "#6b7280", // Gray
  "Ischémie": "#ef4444", // Red
  "Hibernation": "#3b82f6", // Blue
  "Non évaluable": "#ffffff" // White
};

const STRESS_STAGES = [
  { id: "repos", label: "Repos" },
  { id: "low_dose", label: "Effort léger" },
  { id: "pic_stress", label: "Pic d'effort" },
  { id: "recuperation", label: "Récupération" }
];

const segmentsDefinition = [
  // Basal (6 segments)
  { id: "basal_anterior", label: "BA", startAngle: -30, endAngle: 30, rIn: 115, rOut: 160 },
  { id: "basal_anterolateral", label: "BAL", startAngle: 30, endAngle: 90, rIn: 115, rOut: 160 },
  { id: "basal_inferolateral", label: "BIL", startAngle: 90, endAngle: 150, rIn: 115, rOut: 160 },
  { id: "basal_inferior", label: "BI", startAngle: 150, endAngle: 210, rIn: 115, rOut: 160 },
  { id: "basal_inferoseptal", label: "BIS", startAngle: 210, endAngle: 270, rIn: 115, rOut: 160 },
  { id: "basal_anteroseptal", label: "BAS", startAngle: 270, endAngle: 330, rIn: 115, rOut: 160 },
  // Mid (6 segments)
  { id: "mid_anterior", label: "MA", startAngle: -30, endAngle: 30, rIn: 70, rOut: 115 },
  { id: "mid_anterolateral", label: "MAL", startAngle: 30, endAngle: 90, rIn: 70, rOut: 115 },
  { id: "mid_inferolateral", label: "MIL", startAngle: 90, endAngle: 150, rIn: 70, rOut: 115 },
  { id: "mid_inferior", label: "MI", startAngle: 150, endAngle: 210, rIn: 70, rOut: 115 },
  { id: "mid_inferoseptal", label: "MIS", startAngle: 210, endAngle: 270, rIn: 70, rOut: 115 },
  { id: "mid_anteroseptal", label: "MAS", startAngle: 270, endAngle: 330, rIn: 70, rOut: 115 },
  // Apical (4 segments)
  { id: "apical_anterior", label: "AA", startAngle: -45, endAngle: 45, rIn: 25, rOut: 70 },
  { id: "apical_lateral", label: "AL", startAngle: 45, endAngle: 135, rIn: 25, rOut: 70 },
  { id: "apical_inferior", label: "AI", startAngle: 135, endAngle: 225, rIn: 25, rOut: 70 },
  { id: "apical_septal", label: "AS", startAngle: 225, endAngle: 315, rIn: 25, rOut: 70 },
  // Apex
  { id: "apex", label: "AP", rIn: 0, rOut: 25 },
];

const outerLabels = [
  { text: "Antérieur", angle: 0 },
  { text: "Antéro-latéral", angle: 60 },
  { text: "Inféro-latéral", angle: 120 },
  { text: "Inférieur", angle: 180 },
  { text: "Inféro-septal", angle: 240 },
  { text: "Antéro-septal", angle: 300 },
];

const SEGMENT_IDS = segmentsDefinition.map((s) => s.id);

/* ------------------------------------------------------------------
   Utility Functions
------------------------------------------------------------------ */

function getColorFromAnomaly(anomaly, isConclusion = false) {
  if (isConclusion) {
    return CONCLUSION_ANOMALY_COLORS[anomaly] || CONCLUSION_ANOMALY_COLORS["Normal"];
  }
  return ANOMALY_COLORS[anomaly] || ANOMALY_COLORS["Normal"];
}

function createAllNormalSegments() {
  const normalData = {};
  segmentsDefinition.forEach((seg) => {
    normalData[seg.id] = "Normal";
  });
  return normalData;
}

function parseInitialValue(value) {
  const log = createLogger('parseInitialValue');
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (e) { 
      log.error("Invalid JSON in value prop:", e);
      return {};
    }
  }
  return value;
}

function isEmpty(obj) {
  return !obj || Object.keys(obj).length === 0;
}

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

function arcPath(cx, cy, rIn, rOut, startAngle, endAngle) {
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
  const p1 = polarToCartesian(cx, cy, rOut, startAngle);
  const p2 = polarToCartesian(cx, cy, rOut, endAngle);
  const p3 = polarToCartesian(cx, cy, rIn, endAngle);
  const p4 = polarToCartesian(cx, cy, rIn, startAngle);

  return [
    `M ${p1.x},${p1.y}`,
    `A ${rOut},${rOut} 0 ${largeArcFlag} 1 ${p2.x},${p2.y}`,
    `L ${p3.x},${p3.y}`,
    `A ${rIn},${rIn} 0 ${largeArcFlag} 0 ${p4.x},${p4.y}`,
    "Z",
  ].join(" ");
}

function simpleArcPath(cx, cy, r, startAngle, endAngle) {
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
  const pStart = polarToCartesian(cx, cy, r, startAngle);
  const pEnd = polarToCartesian(cx, cy, r, endAngle);
  return [
    `M ${pStart.x},${pStart.y}`,
    `A ${r},${r} 0 ${largeArcFlag} 1 ${pEnd.x},${pEnd.y}`,
  ].join(" ");
}

function formatSegmentName(segmentId) {
  const [level, position] = segmentId.split('_');
  
  const levelMap = {
    'basal': 'basale',
    'mid': 'moyenne',
    'apical': 'apicale',
    'apex': 'apex'
  };

  const positionMap = {
    'anterior': 'antérieure',
    'anteroseptal': 'antéro-septale',
    'inferoseptal': 'inféro-septale',
    'inferior': 'inférieure',
    'inferolateral': 'inféro-latérale',
    'anterolateral': 'antéro-latérale',
    'septal': 'septale',
    'lateral': 'latérale'
  };

  if (segmentId === 'apex') {
    return "de l'apex";
  }

  return `de la paroi ${positionMap[position]} ${levelMap[level]}`;
}

// Parse structured data for stress test (4 stages + conclusion)
function parseStressStructuredData(structuredData) {
  // If the data is already structured for stress stages
  if (structuredData && typeof structuredData === 'object') {
    // Check if it already has stress stage structure
    if (structuredData.repos || structuredData.low_dose || structuredData.pic_stress || structuredData.recuperation) {
      // Handle legacy "stress_leger" key
      if (structuredData.stress_leger && !structuredData.low_dose) {
        structuredData.low_dose = structuredData.stress_leger;
        delete structuredData.stress_leger;
      }
      
      // Initialize conclusion if not present
      if (!structuredData.conclusion) {
        structuredData.conclusion = createAllNormalSegments();
      }
      
      return structuredData;
    }
    
    // Otherwise, use the same data for all stages (fallback)
    return {
      repos: structuredData,
      low_dose: structuredData,
      pic_stress: structuredData,
      recuperation: structuredData,
      conclusion: createAllNormalSegments()
    };
  }
  
  // Return empty stages
  const emptyData = createAllNormalSegments();
  return {
    repos: emptyData,
    low_dose: emptyData,
    pic_stress: emptyData,
    recuperation: emptyData,
    conclusion: emptyData
  };
}

// Convert stress data back to structured format for saving
function convertStressDataToStructured(stressData) {
  return {
    repos: stressData.repos || {},
    low_dose: stressData.low_dose || {},
    pic_stress: stressData.pic_stress || {},
    recuperation: stressData.recuperation || {},
    conclusion: stressData.conclusion || {}
  };
}

/* ------------------------------------------------------------------
   SimpleBullseyeDiagram Component (for small diagrams without labels)
------------------------------------------------------------------ */

function SimpleBullseyeDiagram({ 
  value = {}, 
  onChange, 
  selectedAnomaly = "Normal", 
  width = 150, 
  height = 150,
  showLabels = false,
  title = "",
  isConclusion = false,
  anomalyColors = null,
  onContextMenu,
  stageId
}) {
  const parsedValue = useMemo(() => {
    let parsed = parseInitialValue(value);
    if (isEmpty(parsed)) {
      parsed = createAllNormalSegments();
    }
    return parsed;
  }, [value]);

  // Mouse overlay tracking
  const containerRef = useRef(null);
  const [mouseOverlay, setMouseOverlay] = useState({ x: 0, y: 0, visible: false });

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    if (!selectedAnomaly || selectedAnomaly === "Normal") {
      if (mouseOverlay.visible) setMouseOverlay(prev => ({ ...prev, visible: false }));
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMouseOverlay({ x, y, visible: true });
  };

  const handleMouseLeave = () => {
    if (mouseOverlay.visible) setMouseOverlay(prev => ({ ...prev, visible: false }));
  };

  const handleSegmentClick = (e, segmentId) => {
    e.stopPropagation();
    const newData = { ...parsedValue, [segmentId]: selectedAnomaly };
    onChange(newData);
  };

  const cx = 200;
  const cy = 200;
  const arcRadius = 180;
  const arcSpreadDegrees = 30;
  const halfSpread = arcSpreadDegrees / 2;

  return (
    <div style={{ textAlign: "center" }}>
      {title && (
        <div style={{ 
          fontSize: "12px", 
          fontWeight: "600", 
          marginBottom: "4px",
          color: "#374151"
        }}>
          {title}
        </div>
      )}
      <div
        ref={containerRef}
        style={{ position: "relative", display: "inline-block" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <svg
          viewBox="0 0 400 400"
          width={width}
          height={height}
          style={{ backgroundColor: "#fff", cursor: onContextMenu ? "context-menu" : "pointer" }}
          onContextMenu={onContextMenu ? (e) => onContextMenu(e, stageId, isConclusion) : undefined}
        >
        {/* Segments */}
        {segmentsDefinition.map((seg) => {
          const anomaly = parsedValue[seg.id] || "Normal";
          const fillColor = anomalyColors ? anomalyColors[anomaly] || "#4ade80" : getColorFromAnomaly(anomaly, isConclusion);

          if (seg.id === "apex") {
            return (
              <g key={seg.id}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={seg.rOut}
                  fill={fillColor}
                  stroke="#1f2937"
                  strokeWidth="2"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => handleSegmentClick(e, seg.id)}
                />
                {showLabels && (
                  <text
                    x={cx}
                    y={cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={15}
                    fill="#000"
                    style={{ pointerEvents: "none", fontWeight: "bold" }}
                  >
                    {seg.label}
                  </text>
                )}
              </g>
            );
          }

          const d = arcPath(cx, cy, seg.rIn, seg.rOut, seg.startAngle, seg.endAngle);
          const midAngle = (seg.startAngle + seg.endAngle) / 2;
          const midRadius = (seg.rIn + seg.rOut) / 2;
          const { x, y } = polarToCartesian(cx, cy, midRadius, midAngle);

          return (
            <g key={seg.id}>
              <path
                d={d}
                fill={fillColor}
                stroke="#1f2937"
                strokeWidth="2"
                style={{ cursor: "pointer" }}
                onClick={(e) => handleSegmentClick(e, seg.id)}
              />
              {showLabels && (
                <text
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={16}
                  fill="#000"
                  style={{ pointerEvents: "none", fontWeight: "bold" }}
                >
                  {seg.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Labels extérieurs with curved text */}
        <defs>
          {outerLabels.map((item, idx) => {
            const startAngle = item.angle - halfSpread;
            const endAngle = item.angle + halfSpread;
            const d = simpleArcPath(cx, cy, arcRadius, startAngle, endAngle);
            return (
              <path
                key={`simple-${idx}`}
                id={`simpleLabelPath-${width}-${idx}`}
                d={d}
                fill="none"
                stroke="none"
              />
            );
          })}
        </defs>

        {outerLabels.map((item, idx) => (
          <text key={idx} fontSize={15} fill="#000" fontWeight="bold">
            <textPath
              href={`#simpleLabelPath-${width}-${idx}`}
              startOffset="50%"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {item.text}
            </textPath>
          </text>
        ))}
      </svg>

      {/* Mouse Overlay */}
      {mouseOverlay.visible && selectedAnomaly && selectedAnomaly !== "Normal" && (
        <div
          style={{
            position: "absolute",
            left: mouseOverlay.x + 10,
            top: mouseOverlay.y - 20,
            background: "#111827",
            color: "#fff",
            padding: "4px 8px",
            borderRadius: 6,
            fontSize: 12,
            boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            gap: 6,
            zIndex: 2
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              backgroundColor: anomalyColors ? anomalyColors[selectedAnomaly] || "#4ade80" : getColorFromAnomaly(selectedAnomaly, isConclusion),
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.5)"
            }}
          />
          {selectedAnomaly}
        </div>
      )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   StressBullseyeView Component (4 diagrams + conclusion for Echo de stress)
------------------------------------------------------------------ */

function StressBullseyeView({ anchorEl, visible, onClose, structuredData = {}, onBullseyeChange, inline = false }) {
  const popperRef = useRef(null);
  const [selectedAnomaly, setSelectedAnomaly] = useState("Normal");
  const [selectedConclusionAnomaly, setSelectedConclusionAnomaly] = useState("Normal");
  const [currentMode, setCurrentMode] = useState("stages"); // "stages" or "conclusion"
  const [contextMenu, setContextMenu] = useState(null); // For right-click menu
  const { notification, showNotification, hideNotification } = useNotification();
  
  // Parse the structured data for 4 stages + conclusion
  const stressData = useMemo(() => parseStressStructuredData(structuredData), [structuredData]);
  const inlineScale = inline ? 0.8 : 1;
  const stageSize = Math.round(220 * inlineScale);
  const conclusionSize = Math.round(280 * inlineScale);

  const { styles, attributes } = usePopper(
    inline ? null : anchorEl,
    inline ? null : popperRef.current,
    {
      placement: "auto",
      modifiers: [
        { name: "offset", options: { offset: [0, 10] } },
        { name: "preventOverflow", options: { padding: 8 } },
      ],
    }
  );

  // Handle Escape key to close
  React.useEffect(() => {
    if (inline) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && visible) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onClose, inline]);

  const handleStageChange = (stage, newData) => {
    const updatedData = {
      ...stressData,
      [stage]: newData
    };
    onBullseyeChange(updatedData);
  };

  const handleConclusionChange = (newData) => {
    const updatedData = {
      ...stressData,
      conclusion: newData
    };
    onBullseyeChange(updatedData);
  };

  // Handle right-click context menu
  const handleContextMenu = (e, stageId, isConclusion = false) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      stageId,
      isConclusion
    });
  };

  const handleContextMenuClick = (anomaly) => {
    if (!contextMenu) return;
    
    const newData = { ...stressData };
    const segments = segmentsDefinition.map(s => s.id);
    
    if (contextMenu.isConclusion) {
      // Apply to conclusion bullseye only
      newData.conclusion = segments.reduce((acc, segment) => {
        acc[segment] = anomaly;
        return acc;
      }, {});
    } else {
      // Apply to specific stage bullseye only
      newData[contextMenu.stageId] = segments.reduce((acc, segment) => {
        acc[segment] = anomaly;
        return acc;
      }, {});
    }
    
    onBullseyeChange(convertStressDataToStructured(newData));
    setContextMenu(null);
  };

  const applyToAllStages = (anomaly) => {
    const newSegmentData = {};
    SEGMENT_IDS.forEach((seg) => {
      newSegmentData[seg] = anomaly;
    });
    
    const updatedData = { ...stressData };
    STRESS_STAGES.forEach(stage => {
      updatedData[stage.id] = newSegmentData;
    });
    
    onBullseyeChange(updatedData);
  };

  const applyToConclusionAll = (anomaly) => {
    const newSegmentData = {};
    SEGMENT_IDS.forEach((seg) => {
      newSegmentData[seg] = anomaly;
    });
    
    const updatedData = {
      ...stressData,
      conclusion: newSegmentData
    };
    
    onBullseyeChange(updatedData);
  };

  const handleCopyImage = async () => {
    const node = document.getElementById("stress-bullseye-wrapper");
    if (!node) return;
    const log = createLogger('handleCopyImage');
    const scale = 2;
    const style = window.getComputedStyle(node);
    const width = parseInt(style.getPropertyValue('width'), 10);
    const height = parseInt(style.getPropertyValue('height'), 10);

    try {
      const dataUrl = await domtoimage.toPng(node, {
        width: width * scale,
        height: height * scale,
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        },
      });
      
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const clipboardItem = new ClipboardItem({ "image/png": blob });
      await navigator.clipboard.write([clipboardItem]);
      showNotification("Image copiée avec succès dans le presse papier", "success");
    } catch (err) {
      log.error("Erreur lors de la copie :", err);
      showNotification("Erreur lors de la copie de l'image", "error");
    }
  };

  const handleDownloadPNG = async () => {
    const node = document.getElementById("stress-bullseye-wrapper");
    if (!node) return;

    const scale = 2;
    const style = window.getComputedStyle(node);
    const width = parseInt(style.getPropertyValue('width'), 10);
    const height = parseInt(style.getPropertyValue('height'), 10);

    try {
      const dataUrl = await domtoimage.toPng(node, {
        width: width * scale,
        height: height * scale,
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        },
      });
      
      const link = document.createElement('a');
      link.download = `bullseye_stress_${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      const log = createLogger('handleDownloadPNG');
      log.error('Error downloading image:', err);
      showNotification("Erreur lors du téléchargement", "error");
    }
  };

  // Calculate which anomalies are present across all stages
  const allAnomalies = useMemo(() => {
    const anomalySet = new Set();

    // Add anomalies from stages
    STRESS_STAGES.forEach(stage => {
      const stageData = stressData[stage.id] || {};
      Object.values(stageData).forEach(anomaly => {
        if (anomaly && anomaly !== "Normal" && typeof anomaly === 'string') {
          anomalySet.add(anomaly);
        }
      });
    });

    // Add anomalies from conclusion
    const conclusionData = stressData.conclusion || {};
    Object.values(conclusionData).forEach(anomaly => {
      if (anomaly && anomaly !== "Normal" && typeof anomaly === 'string') {
        anomalySet.add(anomaly);
      }
    });

    return Array.from(anomalySet);
  }, [stressData]);

  if (!inline && !visible) return null;

  return (
    <div
      ref={popperRef}
      style={inline ? {
        background: "#fff",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        padding: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        width: "100%",
        overflow: "auto",
        minWidth: "auto",
        minHeight: "auto"
      } : {
        ...styles.popper,
        zIndex: 9999,
        background: "#fff",
        border: "1px solid #d1d5db",
        borderRadius: 8,
        padding: "12px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        maxWidth: "90vw",
        maxHeight: "90vh",
        overflow: "auto",
        resize: "both",
        minWidth: "800px",
        minHeight: "400px",
      }}
      {...(!inline ? attributes.popper : {})}
      onClick={inline ? undefined : (e) => e.stopPropagation()}
    >
      {notification && (
        <Notification
          key={notification.id}
          id={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}
      
      {/* Header */}
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", gap: "8px" }}>
          {!inline && (
            <Button
              onClick={onClose}
              variant="secondary"
              icon={FaTimes}
            >
              Fermer
            </Button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div style={{ display: "flex", gap: 12, flexDirection: "column", overflowX: "auto" }}>
        {/* Container for all content */}
        <div style={{ background: "#fff", padding: "8px", minWidth: "fit-content" }}>
          {/* All content in one row */}
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
            {/* Wrapper for image capture - only includes the bullseyes */}
            <div id="stress-bullseye-wrapper" style={{ display: "flex", flexDirection: "column", gap: Math.round(16 * inlineScale) }}>
              {/* All bullseyes in one row */}
              <div style={{ display: "flex", gap: Math.round(20 * inlineScale), alignItems: "center" }}>
                {/* 4 Stage Bullseyes */}
                <div style={{ 
                  display: "grid", 
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: Math.round(16 * inlineScale)
                }}>
                  {STRESS_STAGES.map((stage) => (
                    <SimpleBullseyeDiagram
                      key={stage.id}
                      title={stage.label}
                      value={stressData[stage.id] || {}}
                      onChange={(newData) => handleStageChange(stage.id, newData)}
                      selectedAnomaly={selectedAnomaly}
                      width={stageSize}
                      height={stageSize}
                      showLabels={true}
                      isConclusion={false}
                      onContextMenu={handleContextMenu}
                      stageId={stage.id}
                    />
                  ))}
                </div>

                {/* Conclusion Bullseye - centered vertically */}
                <div style={{
                  borderLeft: "2px solid #e5e7eb",
                  paddingLeft: Math.round(20 * inlineScale),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <SimpleBullseyeDiagram
                    title="Conclusion"
                    value={stressData.conclusion || {}}
                    onChange={handleConclusionChange}
                    selectedAnomaly={selectedConclusionAnomaly}
                    width={conclusionSize}
                    height={conclusionSize}
                    showLabels={true}
                    isConclusion={true}
                    anomalyColors={CONCLUSION_ANOMALY_COLORS}
                    onContextMenu={handleContextMenu}
                    stageId="conclusion"
                  />
                </div>
              </div>

                {/* Legends in one row at the bottom - separated */}
                <div style={{ display: "flex", gap: Math.round(30 * inlineScale), justifyContent: "center" }}>
                  {/* Legend for stages */}
                  {(() => {
                    const stageAnomalies = new Set();
                    STRESS_STAGES.forEach(stage => {
                      const stageData = stressData[stage.id] || {};
                      Object.values(stageData).forEach(anomaly => {
                        if (anomaly && anomaly !== "Normal" && typeof anomaly === 'string') {
                          stageAnomalies.add(anomaly);
                        }
                      });
                    });
                    const stageAnomaliesArray = Array.from(stageAnomalies);

                    if (stageAnomaliesArray.length > 0) {
                      return (
                        <div style={{
                          padding: "6px 10px",
                          border: "1px solid #e5e7eb",
                          borderRadius: 5,
                          background: "#f9fafb",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12
                        }}>
                          {stageAnomaliesArray.map((anom) => (
                            <div key={anom} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <div
                                style={{
                                  width: 10,
                                  height: 10,
                                  backgroundColor: getColorFromAnomaly(anom),
                                  border: "1px solid #d1d5db",
                                  borderRadius: 2,
                                }}
                              />
                              <span style={{ fontSize: 10 }}>{anom}</span>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Legend for conclusion */}
                  {(() => {
                    const conclusionAnomalies = new Set();
                    const conclusionData = stressData.conclusion || {};
                    Object.values(conclusionData).forEach(anomaly => {
                      if (anomaly && anomaly !== "Normal" && typeof anomaly === 'string') {
                        conclusionAnomalies.add(anomaly);
                      }
                    });
                    const conclusionAnomaliesArray = Array.from(conclusionAnomalies);

                    if (conclusionAnomaliesArray.length > 0) {
                      return (
                        <div style={{
                          padding: "6px 10px",
                          border: "1px solid #e5e7eb",
                          borderRadius: 5,
                          background: "#f9fafb",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12
                        }}>
                          {conclusionAnomaliesArray.map((anom) => (
                            <div key={anom} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <div
                                style={{
                                  width: 10,
                                  height: 10,
                                  backgroundColor: getColorFromAnomaly(anom, true),
                                  border: "1px solid #d1d5db",
                                  borderRadius: 2,
                                }}
                              />
                              <span style={{ fontSize: 10 }}>{anom}</span>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>


            {/* Anomaly selectors - on the right side in column layout */}
            <div style={{ 
              display: "flex", 
              flexDirection: "column",
              gap: 20
            }}>
              {/* Stage anomaly selector */}
              <div style={{ 
                flex: "0 0 auto",
                padding: "12px",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: "#f9fafb"
              }}>
                <h5 style={{ margin: "0 0 6px 0", fontSize: "12px", fontWeight: "500", color: "#374151" }}>
                  Stades
                </h5>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 3 }}>
                  {ANOMALIES.map((anom) => (
                    <button
                      key={anom}
                      onClick={() => setSelectedAnomaly(anom)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 8px",
                        cursor: "pointer",
                        border: selectedAnomaly === anom ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                        background: selectedAnomaly === anom ? "#eff6ff" : "#fff",
                        borderRadius: 4,
                        fontSize: 14,
                        transition: "all 0.2s",
                      }}
                    >
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: getColorFromAnomaly(anom),
                          border: "1px solid #d1d5db",
                          borderRadius: 2,
                        }}
                      />
                      {anom}
                    </button>
                  ))}
                </div>
              </div>

              {/* Conclusion anomaly selector */}
              <div style={{ 
                flex: "0 0 auto",
                padding: "12px",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: "#f9fafb"
              }}>
                <h5 style={{ margin: "0 0 6px 0", fontSize: "12px", fontWeight: "500", color: "#374151" }}>
                  Conclusion
                </h5>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 3 }}>
                  {CONCLUSION_ANOMALIES.map((anom) => (
                    <button
                      key={anom}
                      onClick={() => setSelectedConclusionAnomaly(anom)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 8px",
                        cursor: "pointer",
                        border: selectedConclusionAnomaly === anom ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                        background: selectedConclusionAnomaly === anom ? "#eff6ff" : "#fff",
                        borderRadius: 4,
                        fontSize: 14,
                        transition: "all 0.2s",
                      }}
                    >
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: getColorFromAnomaly(anom, true),
                          border: "1px solid #d1d5db",
                          borderRadius: 2,
                        }}
                      />
                      {anom}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Context Menu */}
      {contextMenu && (
        <div
          style={{
            position: "fixed",
            left: contextMenu.x,
            top: contextMenu.y,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 8,
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            zIndex: 10000,
            minWidth: 150
          }}
          onMouseLeave={() => setContextMenu(null)}
        >
          <div style={{ fontSize: 12, fontWeight: "600", marginBottom: 8, color: "#374151" }}>
            Appliquer à ce bullseye
          </div>
          {(contextMenu.isConclusion ? CONCLUSION_ANOMALIES : ANOMALIES).map((anomaly) => (
            <button
              key={anomaly}
              onClick={() => handleContextMenuClick(anomaly)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "4px 8px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 11,
                textAlign: "left",
                borderRadius: 4,
                transition: "background 0.2s"
              }}
              onMouseEnter={(e) => e.target.style.background = "#f3f4f6"}
              onMouseLeave={(e) => e.target.style.background = "transparent"}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: contextMenu.isConclusion 
                    ? getColorFromAnomaly(anomaly, true)
                    : getColorFromAnomaly(anomaly),
                  border: "1px solid #d1d5db",
                  borderRadius: 2
                }}
              />
              {anomaly}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const quickActionStyle = {
  padding: "6px 10px",
  border: "1px solid #e5e7eb",
  background: "#f9fafb",
  textAlign: "left",
  cursor: "pointer",
  fontSize: "12px",
  borderRadius: 4,
  transition: "all 0.2s",
  "&:hover": {
    background: "#f3f4f6",
  }
};


/* ------------------------------------------------------------------
   Enhanced PopoverBullseye Component with Echo de stress support
------------------------------------------------------------------ */

function PopoverBullseye({ btnRef, structuredData, onBullseyeChange, reportType }) {
  const [showBullseye, setShowBullseye] = useState(false);
  // Check if report type contains both "echo" and "stress"
  const isStressTest = reportType && 
    reportType.toLowerCase().includes("echo") && 
    reportType.toLowerCase().includes("stress");

  const toggleBullseye = () => {
    setShowBullseye(prev => !prev);
  };

  return (
    <div>
      <Button icon={FaChartLine} variant="orange" ref={btnRef} onClick={toggleBullseye}>
        Schéma cinétique
      </Button>

      {isStressTest ? (
        <StressBullseyeView
          anchorEl={btnRef.current}
          visible={showBullseye}
          onClose={() => setShowBullseye(false)}
          structuredData={structuredData}
          onBullseyeChange={onBullseyeChange}
        />
      ) : (
        <OriginalEchoReportView
          anchorEl={btnRef.current}
          visible={showBullseye}
          onClose={() => setShowBullseye(false)}
          structuredData={structuredData}
          onBullseyeChange={onBullseyeChange}
        />
      )}
    </div>
  );
}

// Export components
export default PopoverBullseye;
export { 
  OriginalBullseyeDiagram as BullseyeDiagram, 
  OriginalEchoReportView as EchoReportView, 
  StressBullseyeView,
  SimpleBullseyeDiagram,
  ANOMALIES, 
  CONCLUSION_ANOMALIES,
  CONCLUSION_ANOMALY_COLORS,
  STRESS_STAGES,
  getColorFromAnomaly, 
  SEGMENT_IDS,
  parseStressStructuredData,
  originalRenderBullseyeImage as renderBullseyeImage 
};